import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useApp } from '../../context/AppContext';
import { addSignature, downloadPDF, getPageCount } from '../../utils/pdfUtils';
import { generateFilename } from '../../utils/fileUtils';
import SignatureModal from './SignatureModal';
import './PDFSigner.css';

// Set up the worker - use the version from node_modules
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PDFSigner = () => {
  const { currentFile, showNotification, setCurrentView, getFileSignatures, updateFileSignatures } = useApp();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);

  // Get signatures from context
  const signatures = currentFile ? getFileSignatures(currentFile.id) : [];
  const setSignatures = (updater) => {
    if (!currentFile) return;
    const newSignatures = typeof updater === 'function' ? updater(signatures) : updater;
    updateFileSignatures(currentFile.id, newSignatures);
  };
  const [pageCount, setPageCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [placingSignature, setPlacingSignature] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [selectedPagesForCopy, setSelectedPagesForCopy] = useState([]);
  const [pageThumbnails, setPageThumbnails] = useState({});
  const [hoveredPage, setHoveredPage] = useState(null);

  useEffect(() => {
    if (!currentFile) return;

    const loadDocument = async () => {
      try {
        const arrayBuffer = await currentFile.file.arrayBuffer();
        const loadedPdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(loadedPdf);
        setPageCount(loadedPdf.numPages);
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadDocument();
    setPageCount(getPageCount(currentFile.pdfDoc));
  }, [currentFile]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(selectedPage + 1);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale: zoom });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, selectedPage, zoom]);

  // Generate thumbnails for all pages
  useEffect(() => {
    if (!pdfDoc) return;

    const generateThumbnails = async () => {
      const thumbnails = {};
      const thumbnailScale = 0.2; // Small scale for thumbnails

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        try {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale: thumbnailScale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext('2d');

          await page.render({ canvasContext: context, viewport }).promise;
          thumbnails[i - 1] = canvas.toDataURL('image/jpeg', 0.7);
        } catch (error) {
          console.error(`Error generating thumbnail for page ${i}:`, error);
        }
      }

      setPageThumbnails(thumbnails);
    };

    generateThumbnails();
  }, [pdfDoc]);

  // Handle mouse move for dragging and resizing
  const handleMouseMove = useCallback((e) => {
    if (dragState) {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom - dragState.offsetX;
      const y = (e.clientY - rect.top) / zoom - dragState.offsetY;

      setSignatures(prev => prev.map(sig =>
        sig.id === dragState.signatureId
          ? { ...sig, position: { ...sig.position, x: Math.max(0, x), y: Math.max(0, y) } }
          : sig
      ));
    } else if (resizeState) {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left) / zoom;
      const mouseY = (e.clientY - rect.top) / zoom;

      setSignatures(prev => prev.map(sig => {
        if (sig.id !== resizeState.signatureId) return sig;

        const { startX, startY, startWidth, startHeight, handle } = resizeState;
        let newX = sig.position.x;
        let newY = sig.position.y;
        let newWidth = sig.position.width;
        let newHeight = sig.position.height;

        const deltaX = mouseX - resizeState.mouseStartX;
        const deltaY = mouseY - resizeState.mouseStartY;

        if (handle.includes('e')) {
          newWidth = Math.max(50, startWidth + deltaX);
        }
        if (handle.includes('w')) {
          newWidth = Math.max(50, startWidth - deltaX);
          newX = startX + (startWidth - newWidth);
        }
        if (handle.includes('s')) {
          newHeight = Math.max(25, startHeight + deltaY);
        }
        if (handle.includes('n')) {
          newHeight = Math.max(25, startHeight - deltaY);
          newY = startY + (startHeight - newHeight);
        }

        return { ...sig, position: { x: newX, y: newY, width: newWidth, height: newHeight } };
      }));
    }
  }, [dragState, resizeState, zoom]);

  const handleMouseUp = useCallback(() => {
    setDragState(null);
    setResizeState(null);
  }, []);

  useEffect(() => {
    if (dragState || resizeState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, resizeState, handleMouseMove, handleMouseUp]);

  const handleSignatureMouseDown = (e, sig) => {
    e.stopPropagation();
    setSelectedSignature(sig.id);

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    setDragState({
      signatureId: sig.id,
      offsetX: mouseX - sig.position.x,
      offsetY: mouseY - sig.position.y
    });
  };

  const handleResizeMouseDown = (e, sig, handle) => {
    e.stopPropagation();
    setSelectedSignature(sig.id);

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    setResizeState({
      signatureId: sig.id,
      handle,
      startX: sig.position.x,
      startY: sig.position.y,
      startWidth: sig.position.width,
      startHeight: sig.position.height,
      mouseStartX: mouseX,
      mouseStartY: mouseY
    });
  };

  if (!currentFile) {
    return (
      <div className="pdf-signer-empty">
        <p>No PDF file loaded</p>
        <button className="btn btn-primary" onClick={() => setCurrentView('upload')}>
          Upload PDF
        </button>
      </div>
    );
  }

  const handleSignatureSave = (signatureDataUrl) => {
    setCurrentSignature(signatureDataUrl);
    setShowSignatureModal(false);
    setPlacingSignature(true);
    showNotification('Click on the PDF to place your signature', 'info');
  };

  const handlePlaceOnCurrentPage = () => {
    if (!currentSignature) {
      showNotification('Please create a signature first', 'warning');
      return;
    }
    setPlacingSignature(true);
    showNotification('Click on the PDF to place your signature', 'info');
  };

  const handleCanvasClick = (e) => {
    if (!placingSignature || !currentSignature) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // Default signature size
    const width = 150;
    const height = 50;

    const newSignature = {
      id: Date.now(),
      signatureDataUrl: currentSignature,
      pageIndex: selectedPage,
      position: { x, y, width, height }
    };

    setSignatures(prev => [...prev, newSignature]);
    setPlacingSignature(false);
    showNotification('Signature placed successfully', 'success');
  };

  const handleRemoveSignature = (id) => {
    setSignatures(prev => prev.filter(sig => sig.id !== id));
    if (selectedSignature === id) {
      setSelectedSignature(null);
    }
    showNotification('Signature removed', 'info');
  };

  const handleOpenPageSelector = (sigId) => {
    const sig = signatures.find(s => s.id === sigId);
    if (!sig) return;

    // Pre-select pages that already have this signature at the same position
    const pagesWithSignature = signatures
      .filter(s =>
        s.signatureDataUrl === sig.signatureDataUrl &&
        s.position.x === sig.position.x &&
        s.position.y === sig.position.y
      )
      .map(s => s.pageIndex);

    setSelectedPagesForCopy(pagesWithSignature);
    setShowPageSelector(true);
  };

  const handleTogglePageForCopy = (pageIndex) => {
    setSelectedPagesForCopy(prev =>
      prev.includes(pageIndex)
        ? prev.filter(p => p !== pageIndex)
        : [...prev, pageIndex]
    );
  };

  const handleSelectAllPages = () => {
    setSelectedPagesForCopy(Array.from({ length: pageCount }, (_, i) => i));
  };

  const handleDeselectAllPages = () => {
    setSelectedPagesForCopy([]);
  };

  const handleApplyToPages = () => {
    const sig = signatures.find(s => s.id === selectedSignature);
    if (!sig) return;

    // Remove existing copies of this signature (same image and position) from pages not selected
    const filteredSignatures = signatures.filter(s => {
      if (s.id === sig.id) return true; // Keep the original
      // Check if it's a copy of the selected signature
      const isCopy = s.signatureDataUrl === sig.signatureDataUrl &&
        s.position.x === sig.position.x &&
        s.position.y === sig.position.y &&
        s.position.width === sig.position.width &&
        s.position.height === sig.position.height;
      if (isCopy) {
        // Keep only if page is still selected
        return selectedPagesForCopy.includes(s.pageIndex);
      }
      return true;
    });

    // Add new copies to selected pages that don't already have this signature
    const existingPages = filteredSignatures
      .filter(s =>
        s.signatureDataUrl === sig.signatureDataUrl &&
        s.position.x === sig.position.x &&
        s.position.y === sig.position.y
      )
      .map(s => s.pageIndex);

    const newSignatures = selectedPagesForCopy
      .filter(pageIndex => !existingPages.includes(pageIndex))
      .map(pageIndex => ({
        id: Date.now() + pageIndex,
        signatureDataUrl: sig.signatureDataUrl,
        pageIndex,
        position: { ...sig.position }
      }));

    setSignatures([...filteredSignatures, ...newSignatures]);
    setShowPageSelector(false);

    const addedCount = newSignatures.length;
    if (addedCount > 0) {
      showNotification(`Signature applied to ${selectedPagesForCopy.length} page${selectedPagesForCopy.length !== 1 ? 's' : ''}`, 'success');
    } else {
      showNotification('Pages updated', 'success');
    }
  };

  const handleSaveSignedPDF = async () => {
    if (signatures.length === 0) {
      showNotification('Please add at least one signature', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      let signedDoc = currentFile.pdfDoc;

      // Add all signatures to the PDF
      for (const sig of signatures) {
        signedDoc = await addSignature(
          signedDoc,
          sig.signatureDataUrl,
          sig.pageIndex,
          sig.position
        );
      }

      const filename = generateFilename(currentFile.name, 'signed');
      await downloadPDF(signedDoc, filename);
      showNotification('Signed PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error saving signed PDF:', error);
      showNotification('Failed to save signed PDF', 'error');
    }

    setIsSaving(false);
  };

  return (
    <div className="pdf-signer-container">
      <div className="signer-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('view')}>
          ← Back to Viewer
        </button>
        <h2>Sign PDF</h2>
        <div className="signer-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowSignatureModal(true)}
            disabled={placingSignature}
          >
            {placingSignature ? 'Placing Signature...' : 'Create Signature'}
          </button>
          {placingSignature && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setPlacingSignature(false);
                showNotification('Signature placement cancelled', 'info');
              }}
            >
              Cancel Placement
            </button>
          )}
        </div>
      </div>

      <div className="signer-content">
        <div className="signer-sidebar">
          <h3>Pages</h3>
          <div className="page-list">
            {Array.from({ length: pageCount }, (_, i) => i).map((pageIndex) => (
              <div
                key={pageIndex}
                className={`page-thumbnail-item ${pageIndex === selectedPage ? 'active' : ''}`}
                onClick={() => setSelectedPage(pageIndex)}
                onMouseEnter={() => setHoveredPage(pageIndex)}
                onMouseLeave={() => setHoveredPage(null)}
              >
                <div className="thumbnail-wrapper">
                  {pageThumbnails[pageIndex] ? (
                    <img
                      src={pageThumbnails[pageIndex]}
                      alt={`Page ${pageIndex + 1}`}
                      className="page-thumbnail"
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <span>{pageIndex + 1}</span>
                    </div>
                  )}
                  {signatures.filter(sig => sig.pageIndex === pageIndex).length > 0 && (
                    <span className="signature-badge thumbnail-badge">
                      {signatures.filter(sig => sig.pageIndex === pageIndex).length}
                    </span>
                  )}
                </div>
                <span className="page-label">Page {pageIndex + 1}</span>

                {/* Hover preview */}
                {hoveredPage === pageIndex && pageThumbnails[pageIndex] && (
                  <div className="page-preview-popup">
                    <img src={pageThumbnails[pageIndex]} alt={`Preview of page ${pageIndex + 1}`} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Current signature section */}
          {currentSignature && (
            <div className="current-signature-section">
              <h3>Your Signature</h3>
              <div className="current-signature-preview">
                <img src={currentSignature} alt="Your signature" />
              </div>
              {!placingSignature && (
                <button
                  className="btn btn-primary btn-sm btn-full"
                  onClick={handlePlaceOnCurrentPage}
                >
                  {`Place on this page (Page ${selectedPage + 1})`}
                </button>
              )}
            </div>
          )}

          {signatures.length > 0 && (
            <div className="signatures-list">
              <h3>Placed Signatures ({signatures.length})</h3>
              {signatures.map((sig) => (
                <div
                  key={sig.id}
                  className={`signature-item ${sig.pageIndex === selectedPage ? 'current-page' : ''}`}
                  onClick={() => setSelectedPage(sig.pageIndex)}
                >
                  <div className="signature-info">
                    <img src={sig.signatureDataUrl} alt="Signature" />
                    <span>Page {sig.pageIndex + 1}</span>
                  </div>
                  <button
                    className="btn btn-ghost remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSignature(sig.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pdf-canvas-area">
          <div className="canvas-controls">
            <div className="zoom-controls">
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.5))}>
                −
              </button>
              <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="btn btn-ghost" onClick={() => setZoom(prev => Math.min(prev + 0.25, 3))}>
                +
              </button>
            </div>
          </div>

          <div className="canvas-wrapper" onClick={() => setSelectedSignature(null)}>
            <div
              ref={containerRef}
              className="canvas-container"
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <canvas
                ref={canvasRef}
                className={`pdf-canvas ${placingSignature ? 'placing-signature' : ''}`}
                onClick={handleCanvasClick}
              />
              {/* Render signatures as interactive overlays */}
              {signatures
                .filter(sig => sig.pageIndex === selectedPage)
                .map(sig => (
                  <div
                    key={sig.id}
                    className={`signature-overlay ${selectedSignature === sig.id ? 'selected' : ''}`}
                    style={{
                      position: 'absolute',
                      left: sig.position.x * zoom,
                      top: sig.position.y * zoom,
                      width: sig.position.width * zoom,
                      height: sig.position.height * zoom,
                      cursor: dragState?.signatureId === sig.id ? 'grabbing' : 'grab'
                    }}
                    onMouseDown={(e) => handleSignatureMouseDown(e, sig)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={sig.signatureDataUrl}
                      alt="Signature"
                      draggable={false}
                      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                    />
                    {selectedSignature === sig.id && (
                      <>
                        {/* Resize handles */}
                        <div className="resize-handle nw" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'nw')} />
                        <div className="resize-handle ne" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'ne')} />
                        <div className="resize-handle sw" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'sw')} />
                        <div className="resize-handle se" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'se')} />
                        <div className="resize-handle n" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'n')} />
                        <div className="resize-handle s" onMouseDown={(e) => handleResizeMouseDown(e, sig, 's')} />
                        <div className="resize-handle e" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'e')} />
                        <div className="resize-handle w" onMouseDown={(e) => handleResizeMouseDown(e, sig, 'w')} />
                        {/* Signature toolbar */}
                        {pageCount > 1 && (
                          <div className="signature-toolbar" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleOpenPageSelector(sig.id)}
                              title="Apply to multiple pages"
                            >
                              Copy to Pages
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleRemoveSignature(sig.id)}
                              title="Remove signature"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
            </div>
            {placingSignature && (
              <div className="placement-hint">
                Click on the PDF to place your signature
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="signer-footer">
        <button
          className="btn btn-success"
          onClick={handleSaveSignedPDF}
          disabled={isSaving || signatures.length === 0}
        >
          {isSaving ? (
            <>
              <span className="spinner"></span>
              Saving...
            </>
          ) : (
            `Download Signed PDF (${signatures.length} Signature${signatures.length !== 1 ? 's' : ''})`
          )}
        </button>
      </div>

      {showSignatureModal && (
        <SignatureModal
          onClose={() => setShowSignatureModal(false)}
          onSave={handleSignatureSave}
        />
      )}

      {showPageSelector && (
        <div className="modal-backdrop" onClick={() => setShowPageSelector(false)}>
          <div className="modal page-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply Signature to Pages</h2>
              <button className="btn btn-ghost close-btn" onClick={() => setShowPageSelector(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="page-selector-hint">Select which pages should have this signature at the same position:</p>
              <div className="page-selector-actions">
                <button className="btn btn-sm btn-ghost" onClick={handleSelectAllPages}>
                  Select All
                </button>
                <button className="btn btn-sm btn-ghost" onClick={handleDeselectAllPages}>
                  Deselect All
                </button>
              </div>
              <div className="page-selector-grid">
                {Array.from({ length: pageCount }, (_, i) => i).map((pageIndex) => (
                  <label
                    key={pageIndex}
                    className={`page-selector-item ${selectedPagesForCopy.includes(pageIndex) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPagesForCopy.includes(pageIndex)}
                      onChange={() => handleTogglePageForCopy(pageIndex)}
                    />
                    <span className="page-number">Page {pageIndex + 1}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowPageSelector(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyToPages}
                disabled={selectedPagesForCopy.length === 0}
              >
                Apply to {selectedPagesForCopy.length} Page{selectedPagesForCopy.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFSigner;
